import * as v2 from '@/core/services/api/v2';

/**
 * O mercado ENTRE JOGADORES.
 *
 * ## Por que o nome é comprido
 *
 * Já existe uma store `market`, e ela é outra coisa: o **mercado do dia** — o
 * peixe favorito, a região quente, sabor que muda uma vez por dia e não vende
 * nada. Chamar esta de `market` também seria trocar um nome ruim por dois nomes
 * iguais, e a primeira pessoa a depurar `market/carregar` perderia meia hora
 * descobrindo qual dos dois estava olhando.
 *
 * ## As três coisas que orientam esta store
 *
 * - **Nenhum número de jogo aqui.** Taxa, comissão, líquido, teto de anúncios e
 *   prazo vêm da API (`previa`). O cliente sabe calcular a taxa — a fórmula está
 *   em `@fishbay/domain` — mas o que ele não tem é quantos anúncios abertos o
 *   jogador já tem, e sem isso a tela ofereceria anunciar para depois o servidor
 *   recusar.
 * - **A vitrine não é cacheada, nem aqui nem no servidor.** Um anúncio que
 *   aparece cinco minutos depois de vendido faz o jogador clicar em comprar e
 *   levar 409. Estoque com cache mente; a lista recarrega ao trocar de filtro e
 *   ao voltar para a aba.
 * - **O erro tem código.** A API responde `{ message, codigo }` e é o `codigo`
 *   que a tela traduz (`market.errors.<codigo>`). Guardar a mensagem do servidor
 *   mostraria português para quem joga em chinês — a mesma convenção da 1.2.
 *
 * ## As três listas são PAGINADAS POR NÚMERO (11/09/2026)
 *
 * Vitrine, meus anúncios e compras guardam a página aberta e o total; o
 * `FishPaginacao` desenha a trilha. Até ali a vitrine acumulava páginas num
 * "ver mais" por cursor, e as outras duas mostravam só as vinte primeiras —
 * sem dizer que havia mais. **Trocar filtro volta à página 1**: a página 4 de
 * "materiais" não é a página 4 de "integrantes".
 */

/** Quantos cartões por página. Par, porque a lista é uma grade de duas colunas. */
export const POR_PAGINA = 12;

/** O código estável do erro, quando houver. É por ele que a tela traduz. */
function codigoDoErro(error) {
  return error?.response?.data?.codigo || null;
}

function mensagemDeErro(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    error?.message ||
    'erro'
  );
}

/**
 * A página que o servidor devolveu, na forma que o controle de páginas lê.
 *
 * `porPagina` vem da RESPOSTA, e não da constante: o controle calcula quantas
 * páginas há com ele, e se o servidor usar outro tamanho (teto, config) a trilha
 * contaria páginas que não existem — ou esconderia as que existem.
 */
const paginaDe = (dados) => ({
  pagina: dados?.pagina ?? 1,
  porPagina: dados?.porPagina ?? POR_PAGINA,
  total: dados?.total ?? 0,
  totalDePaginas: dados?.totalDePaginas ?? 1,
});

/** Os filtros da vitrine sem nenhum ligado. */
export const FILTROS_VAZIOS = {
  tipo: null,
  moeda: null,
  ordem: 'recentes',
  busca: '',
  raridade: null,
  guilda: null,
  rank: null,
  precoMin: null,
  precoMax: null,
};

const state = {
  /** A página atual da vitrine. */
  anuncios: [],
  vitrine: { pagina: 1, porPagina: POR_PAGINA, total: 0, totalDePaginas: 1 },

  /** Os filtros. Moram na store e não no componente para sobreviver à aba. */
  filtros: { ...FILTROS_VAZIOS },

  meusAnuncios: [],
  meus: { pagina: 1, porPagina: POR_PAGINA, total: 0, totalDePaginas: 1 },
  /** O estado filtrado em "Meus anúncios": `todos`, `open`, `sold`, `cancelled`, `expired`. */
  statusDosMeus: 'todos',
  /** Abertos agora e o teto — a tela precisa dos dois para o aviso. */
  abertos: 0,
  teto: 0,

  compras: [],
  paginaDasCompras: { pagina: 1, porPagina: POR_PAGINA, total: 0, totalDePaginas: 1 },

  /** A prévia da taxa do formulário. Vem do servidor; ver o cabeçalho. */
  previa: null,

  carregando: false,
  erro: null,
};

const mutations = {
  SET_VITRINE(state, dados) {
    state.anuncios = dados.anuncios || [];
    state.vitrine = paginaDe(dados);
  },
  SET_FILTROS(state, filtros) {
    state.filtros = { ...state.filtros, ...filtros };
  },
  SET_MEUS(state, dados) {
    state.meusAnuncios = dados.anuncios || [];
    state.meus = paginaDe(dados);
    state.abertos = dados.abertos ?? 0;
    state.teto = dados.teto ?? 0;
  },
  SET_STATUS_DOS_MEUS(state, status) {
    state.statusDosMeus = status;
  },
  SET_COMPRAS(state, dados) {
    state.compras = dados.compras || [];
    state.paginaDasCompras = paginaDe(dados);
  },
  SET_PREVIA(state, previa) {
    state.previa = previa;
  },
  SET_CARREGANDO(state, valor) {
    state.carregando = valor;
  },
  SET_ERRO(state, erro) {
    state.erro = erro;
  },
};

/**
 * Recarrega TODOS os estoques de onde o mercado tira ou para onde ele entrega.
 *
 * Uma lista só para as três ações (anunciar, comprar, cancelar), e não uma
 * cópia em cada: quando a poção e o apetrecho entraram no mercado (11/09/2026),
 * eram três lugares para lembrar do baú do pescador — e esquecer um deixava a
 * mochila mostrando a poção que acabou de ir para o balcão. Cada chamada é
 * tolerante a falha: quem nunca explorou recebe lista vazia, e isso não é erro.
 */
function recarregarEstoques(dispatch) {
  const raiz = { root: true };
  return [
    dispatch('exploration/carregarMateriais', null, raiz).catch(() => {}),
    dispatch('exploration/carregarEstoque', null, raiz).catch(() => {}),
    dispatch('exploration/carregar', null, raiz).catch(() => {}),
    dispatch('pescador/carregarApetrechos', null, raiz).catch(() => {}),
    dispatch('pescador/carregarPocoes', null, raiz).catch(() => {}),
  ];
}

/** Toda ação que escreve passa por aqui: limpa erro, marca carregando, guarda o código. */
async function executar({ commit }, fn) {
  commit('SET_ERRO', null);
  commit('SET_CARREGANDO', true);
  try {
    return await fn();
  } catch (error) {
    commit('SET_ERRO', { codigo: codigoDoErro(error), mensagem: mensagemDeErro(error) });
    throw error;
  } finally {
    commit('SET_CARREGANDO', false);
  }
}

/**
 * Os filtros como a query os leva: só os ligados.
 *
 * `null` e `''` não viram parâmetro — o zod do servidor recusa `''` como tipo
 * inválido em vez de tratá-lo como "sem filtro".
 */
function paramsDaVitrine(filtros, pagina) {
  const params = { ordem: filtros.ordem, pagina, porPagina: POR_PAGINA };
  for (const chave of ['tipo', 'moeda', 'busca', 'raridade', 'guilda', 'rank', 'precoMin', 'precoMax']) {
    const valor = filtros[chave];
    if (valor !== null && valor !== undefined && valor !== '') params[chave] = valor;
  }
  return params;
}

const actions = {
  /**
   * A vitrine, numa página. Sem `pagina`, recarrega a que está aberta — é o que
   * comprar, cancelar e voltar para a aba pedem.
   *
   * `ordem: 'preco'` sem moeda o servidor recusa com 400, e de propósito:
   * comparar 30 Parallelium com 12 000 de ouro não quer dizer nada. A tela nunca
   * chega lá porque escolher "mais baratos" já força a escolha da moeda — mas se
   * chegar, o erro é legível. A faixa de preço tem a mesma regra.
   */
  async carregarVitrine({ commit, state, rootState }, { pagina } = {}) {
    const playerId = rootState.player?.id;
    if (!playerId) return null;

    const params = paramsDaVitrine(state.filtros, pagina ?? state.vitrine.pagina);

    return executar({ commit }, async () => {
      const dados = await v2.vitrineDoMercado(playerId, params);
      commit('SET_VITRINE', dados);
      return dados;
    });
  },

  /** Troca filtro e volta à página 1: são outros anúncios, e a página 4 deles é outra. */
  async filtrar({ commit, dispatch }, filtros) {
    commit('SET_FILTROS', filtros);
    return dispatch('carregarVitrine', { pagina: 1 });
  },

  /** Desliga todos os filtros de uma vez. */
  async limparFiltros({ commit, dispatch }) {
    commit('SET_FILTROS', { ...FILTROS_VAZIOS });
    return dispatch('carregarVitrine', { pagina: 1 });
  },

  /**
   * Meus anúncios. `status` troca o filtro e volta à página 1; sem ele, fica o
   * filtro que estava. Sem `pagina`, recarrega a aberta.
   */
  async carregarMeusAnuncios({ commit, state, rootState }, { pagina, status } = {}) {
    const playerId = rootState.player?.id;
    if (!playerId) return null;

    const trocouStatus = status !== undefined && status !== state.statusDosMeus;
    if (trocouStatus) commit('SET_STATUS_DOS_MEUS', status);

    return executar({ commit }, async () => {
      const dados = await v2.meusAnuncios(playerId, {
        status: state.statusDosMeus,
        pagina: trocouStatus ? 1 : (pagina ?? state.meus.pagina),
        porPagina: POR_PAGINA,
      });
      commit('SET_MEUS', dados);
      return dados;
    });
  },

  async carregarCompras({ commit, state, rootState }, { pagina } = {}) {
    const playerId = rootState.player?.id;
    if (!playerId) return null;

    return executar({ commit }, async () => {
      const dados = await v2.minhasCompras(playerId, {
        pagina: pagina ?? state.paginaDasCompras.pagina,
        porPagina: POR_PAGINA,
      });
      commit('SET_COMPRAS', dados);
      return dados;
    });
  },

  /**
   * A prévia da taxa. Chamada a cada mudança de preço ou de quantidade no
   * formulário — a taxa é do LOTE, então as duas mudam a conta.
   *
   * Falha em silêncio de propósito: se o preço digitado ainda é inválido (zero,
   * ou o lote acima do teto), o servidor responde 400 e a prévia fica nula — o
   * formulário já mostra a recusa pelo próprio campo, e um erro vermelho a cada
   * dígito digitado seria a tela brigando com quem escreve.
   */
  async pedirPrevia({ commit, rootState }, { preco, quantidade = 1, moeda }) {
    const playerId = rootState.player?.id;
    if (!playerId || !preco) {
      commit('SET_PREVIA', null);
      return null;
    }
    try {
      const previa = await v2.previaDeAnuncio(playerId, { preco, quantidade, moeda });
      commit('SET_PREVIA', previa);
      return previa;
    } catch {
      commit('SET_PREVIA', null);
      return null;
    }
  },

  /**
   * Anuncia, e recarrega o que mudou.
   *
   * Recarrega o **armazém** junto, e é a parte fácil de esquecer: anunciar tira
   * material do estoque de verdade (escrow), e a tela de materiais mostraria a
   * quantidade antiga até alguém recarregar a página. `player` entra porque a
   * taxa saiu do saldo.
   */
  async anunciar({ commit, dispatch, rootState }, entrada) {
    const playerId = rootState.player?.id;
    if (!playerId) return null;

    return executar({ commit }, async () => {
      const anuncio = await v2.anunciarNoMercado(playerId, entrada);
      await Promise.all([
        dispatch('carregarMeusAnuncios', { pagina: 1 }),
        dispatch('player/loadPlayerState', playerId, { root: true }).catch(() => {}),
        ...recarregarEstoques(dispatch),
      ]);
      return anuncio;
    });
  },

  /**
   * Compra `quantidade` unidades — uma, parte do lote ou o lote todo (`0054`).
   *
   * Recarrega a vitrine porque o anúncio comprado muda para todo mundo: sai da
   * lista se esgotou, e mostra o que resta se não. Sem isso o cartão fica com o
   * restante antigo, e o segundo clique pede unidades que já foram.
   */
  async comprar({ commit, dispatch, rootState }, { anuncioId, quantidade = 1 }) {
    const playerId = rootState.player?.id;
    if (!playerId) return null;

    return executar({ commit }, async () => {
      const compra = await v2.comprarNoMercado(playerId, anuncioId, quantidade);
      await Promise.all([
        dispatch('carregarVitrine'),
        dispatch('player/loadPlayerState', playerId, { root: true }).catch(() => {}),
        dispatch('employees/loadEmployees', playerId, { root: true }).catch(() => {}),
        ...recarregarEstoques(dispatch),
      ]);
      return compra;
    });
  },

  /** Cancela. Devolve o que restava guardado; a taxa não volta. */
  async cancelar({ commit, dispatch, rootState }, anuncioId) {
    const playerId = rootState.player?.id;
    if (!playerId) return null;

    return executar({ commit }, async () => {
      await v2.cancelarAnuncio(playerId, anuncioId);
      await Promise.all([
        dispatch('carregarMeusAnuncios'),
        dispatch('carregarVitrine'),
        ...recarregarEstoques(dispatch),
      ]);
    });
  },

  limparErro({ commit }) {
    commit('SET_ERRO', null);
  },
};

const getters = {
  /** `true` quando o jogador não pode abrir outro anúncio. */
  tetoAtingido: (state) => state.teto > 0 && state.abertos >= state.teto,
  /** Os abertos DA PÁGINA — os únicos em que ele pode agir. O servidor os põe primeiro. */
  meusAbertos: (state) => state.meusAnuncios.filter((a) => a.status === 'open'),
  /** O histórico da página: vendeu, cancelou ou venceu. */
  meuHistorico: (state) => state.meusAnuncios.filter((a) => a.status !== 'open'),
  /** Quantos filtros além do tipo estão ligados — o que o botão "Mais filtros" conta. */
  filtrosExtrasLigados: (state) =>
    ['raridade', 'guilda', 'rank', 'precoMin', 'precoMax'].filter(
      (chave) => state.filtros[chave] !== null && state.filtros[chave] !== '',
    ).length,
};

export default { namespaced: true, state, mutations, actions, getters };
